import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ArrowRight, Scale, Filter, Search as SearchIcon } from "lucide-react";
import api from "@/lib/api";

const STATUS_LABEL = {
    open: "Open",
    stayed: "Stayed",
    settled: "Settled",
    closed: "Closed",
    archived: "Archived",
};

const STATUS_TONE = {
    open:     "bg-emerald-50  text-emerald-700  border-emerald-200",
    stayed:   "bg-amber-50    text-amber-800    border-amber-200",
    settled:  "bg-blue-50     text-blue-700     border-blue-200",
    closed:   "bg-slate-100   text-slate-700    border-slate-200",
    archived: "bg-slate-100   text-slate-500    border-slate-200",
};

const TYPES = [
    "commercial_contract", "litigation", "employment", "ip",
    "corporate_governance", "compliance", "claims", "gov_request", "other",
];

export default function LegalMattersList() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [q, setQ] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const params = {};
                if (statusFilter) params.status = statusFilter;
                if (typeFilter) params.matter_type = typeFilter;
                const { data } = await api.get("/legal/matters", { params });
                setItems(data.items || []);
                setError("");
            } catch (e) {
                setError(e.response?.data?.detail?.message || e.response?.data?.detail || "Failed to load matters.");
            } finally {
                setLoading(false);
            }
        })();
    }, [statusFilter, typeFilter]);

    const filtered = items.filter((m) => {
        if (!q.trim()) return true;
        const s = q.trim().toLowerCase();
        return (
            (m.title || "").toLowerCase().includes(s) ||
            (m.matter_number || "").toLowerCase().includes(s)
        );
    });

    return (
        <div className="h-full overflow-y-auto wfos-scrollbar" data-testid="legal-matters-list-page">
            <div className="max-w-6xl mx-auto p-8">
                <div className="flex items-end justify-between">
                    <div>
                        <div className="wfos-label text-gray-500">Legal · Matters</div>
                        <h1 className="mt-2 text-3xl font-medium tracking-tight text-gray-900 flex items-center gap-2">
                            <Scale className="w-6 h-6 text-[#16a34a]" strokeWidth={1.75} />
                            All matters
                        </h1>
                        <p className="mt-1 text-sm text-gray-500">
                            {items.length} matter{items.length === 1 ? "" : "s"} in this workspace.
                        </p>
                    </div>
                    <Link
                        to="/app/legal/matters/new"
                        data-testid="new-matter-btn"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#16a34a] px-4 h-10 text-sm font-medium text-white hover:bg-[#15803d]"
                    >
                        <Plus className="w-4 h-4" strokeWidth={2} /> New matter
                    </Link>
                </div>

                {/* Filters */}
                <div className="mt-6 flex flex-wrap items-center gap-2">
                    <div className="relative">
                        <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" strokeWidth={2} />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search matter # or title…"
                            data-testid="matter-search"
                            className="rounded-lg border border-[#e8e8e3] bg-white pl-8 pr-3 h-9 text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#16a34a] w-72"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        data-testid="matter-filter-status"
                        className="rounded-lg border border-[#e8e8e3] bg-white px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                    >
                        <option value="">All statuses</option>
                        {Object.entries(STATUS_LABEL).map(([k, v]) => (
                            <option key={k} value={k}>{v}</option>
                        ))}
                    </select>
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        data-testid="matter-filter-type"
                        className="rounded-lg border border-[#e8e8e3] bg-white px-3 h-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                    >
                        <option value="">All types</option>
                        {TYPES.map((t) => (
                            <option key={t} value={t}>
                                {t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                            </option>
                        ))}
                    </select>
                </div>

                {error && (
                    <div className="mt-6 rounded-lg border border-rose-200 bg-rose-50 p-3 text-[13px] text-rose-800" data-testid="matter-list-error">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="mt-10 text-sm text-gray-500">Loading matters…</div>
                ) : filtered.length === 0 ? (
                    <div className="mt-10 wfos-card p-8 text-center" data-testid="matters-empty-state">
                        <div className="text-lg font-medium text-gray-900">No matters yet</div>
                        <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
                            Open your first matter — draft complaints, track deadlines, log evidence, all
                            supervised by attorneys.
                        </p>
                        <Link
                            to="/app/legal/matters/new"
                            className="mt-4 inline-flex items-center gap-1 rounded-lg bg-[#16a34a] px-3.5 py-2 text-sm font-medium text-white hover:bg-[#15803d]"
                            data-testid="matters-empty-state-open-first"
                        >
                            Open first matter <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                        </Link>
                    </div>
                ) : (
                    <div className="mt-6 border border-[#e8e8e3] rounded-xl overflow-hidden bg-white">
                        <table className="w-full text-[13.5px]">
                            <thead className="bg-[#fafaf7] border-b border-[#e8e8e3]">
                                <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                                    <th className="px-4 py-2.5">Matter #</th>
                                    <th className="px-4 py-2.5">Title</th>
                                    <th className="px-4 py-2.5">Type</th>
                                    <th className="px-4 py-2.5">Jurisdiction</th>
                                    <th className="px-4 py-2.5">Status</th>
                                    <th className="px-4 py-2.5">Opened</th>
                                    <th className="px-4 py-2.5"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((m) => (
                                    <tr key={m.id} className="border-t border-[#f1f1ea] hover:bg-[#fafaf7]" data-testid={`matter-row-${m.id}`}>
                                        <td className="px-4 py-3 font-mono text-[12px] text-gray-900 tabular-nums">
                                            {m.matter_number}
                                        </td>
                                        <td className="px-4 py-3 text-gray-900">
                                            <Link
                                                to={`/app/legal/matters/${m.id}`}
                                                className="hover:text-[#16a34a]"
                                                data-testid={`matter-link-${m.id}`}
                                            >
                                                {m.title}
                                            </Link>
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">
                                            {(m.matter_type || "").replace(/_/g, " ")}
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">
                                            {m.jurisdiction || "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium ${STATUS_TONE[m.status] || "bg-slate-100 text-slate-600"}`}
                                            >
                                                {STATUS_LABEL[m.status] || m.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-gray-500 text-[12.5px]">
                                            {new Date(m.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <Link
                                                to={`/app/legal/matters/${m.id}`}
                                                className="text-[#16a34a] hover:text-[#15803d]"
                                                data-testid={`matter-row-arrow-${m.id}`}
                                            >
                                                <ArrowRight className="w-4 h-4 inline" strokeWidth={2} />
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <div className="h-16" />
            </div>
        </div>
    );
}
